import React, { useEffect, useState } from 'react';
import SavedBankAccountSelect, { useCustomerBankAutofill } from '../../customers/components/SavedBankAccountSelect';
import IfscInput, { IFSC_PATTERN, bankBranchLabel } from '../../customers/components/IfscInput';
import { useNavigate, useParams } from 'react-router-dom';
import { createWalkInCase, updateWalkInCase, getWalkInCaseById } from '../api';
import { fetchCustomers, getCustomer } from '../../customers/api';
import { fetchAgents } from '../../agents/api';
import { uploadRawImage } from '../../billing/api'; 
import {
  Form, Input, InputNumber, AutoComplete, Button, Card, Typography,
  Row, Col, Spin, message, Space, Table, Divider, Tag, Tooltip, Switch, Radio, Alert,
} from 'antd';
import {
  CloseOutlined, PlusOutlined, GoldOutlined, SyncOutlined, UserOutlined, CameraOutlined,
  ProfileOutlined, BankOutlined, PhoneOutlined, TeamOutlined, CheckOutlined,
} from '@ant-design/icons';
import { FormSection, CustomerSelect, AgentSelect } from '../../../components/CaseForm';
import { fetchHallmarkGoldRate, invalidateGoldPriceCache } from '../../../services/goldPriceService';
import { fetchSilverRates } from '../../../services/silverPriceService';
import { useKaratDeductions } from '../../system/hooks/useKaratDeductions';
import {
  recalcCaseGoldItem, calculateCaseGoldTotals, calculateCaseProcessingFee, calculateCaseFinalAmount,
  formatWeight4, formatAmount4, calculateCaseRoundOff, formatRoundOff,
} from '../../../utils/goldCalculation';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUnsavedCaseGuard, hasGoldItemInput } from '../../../hooks/useUnsavedCaseGuard';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchGstToggleVisibility, fetchBranchProcessingFeeToggleVisibility } from '../../treasury/api';
import GoldImageCapture from '../../billing/components/GoldImageCapture';
import MeltingConsentButton from '../../../components/MeltingConsentForm';
import 'bootstrap/dist/css/bootstrap.min.css';

const { Title, Text } = Typography;

const GOLD_OWNERSHIP_OPTIONS = ['Self', 'Spouse', 'Parents', 'Other'].map((value) => ({ value }));

const formatWholeAmount = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);

const formatRoundingAmount = (amount) => {
  if (amount > 0) {
    return `+₹ ${formatWholeAmount(amount)}`;
  } else if (amount < 0) {
    return `-₹ ${formatWholeAmount(Math.abs(amount))}`;
  }
  return `₹ ${formatWholeAmount(amount)}`;
};

// Processing fee is exact (not rounded to a rupee), so it shows 4 decimals.
const formatProcessingFeeDisplay = (amount) => {
  if (amount > 0) {
    return `-₹ ${formatAmount4(amount)}`;
  } else if (amount < 0) {
    return `+₹ ${formatAmount4(Math.abs(amount))}`;
  }
  return `₹ ${formatAmount4(amount)}`;
};

const DEFAULT_PURITY = 'Gold 916'; // Default purity selection


// Silver: rates are manual (System → Silver Rates), no live feed, no karat deduction.
const SILVER_DEFAULT_PURITY = 'Silver 999';

const EMPTY_GOLD_ITEM = {
  qty: 1,
  itemName: '',
  purityName: DEFAULT_PURITY,  // Track which purity is selected
  purity: 0,  // Never prefilled — typed in after testing
  grossWeight: 0,
  stoneWaxDeduction: 0,
  dustDeduction: 0,
  netWeight: 0,
  ratePerGram: 0,
  totalPrice: 0,
};

export default function WalkInCaseForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [goldItems, setGoldItems] = useState([{ ...EMPTY_GOLD_ITEM }]);
  const [goldImages, setGoldImages] = useState([]);
  // A new case lives only in this form until Create — warn before losing it.
  const allowLeave = useUnsavedCaseGuard(!isEdit && hasGoldItemInput(goldItems), 'Walk-In Purchase Case');
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  // Full customer doc behind the selected id — the melting-consent form needs
  // address and ID proof, which the <Select> options don't carry.
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Case identity for the consent form; blank until the case is saved.
  const [caseMeta, setCaseMeta] = useState({ code: null, date: null });
  const [agents, setAgents] = useState([]);
  const [agentCommissionPercent, setAgentCommissionPercent] = useState(0);
  const [processingFee, setProcessingFee] = useState(0);
  const [roundingUpAmount, setRoundingUpAmount] = useState(0);
  const [liveRates, setLiveRates] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [metalType, setMetalType] = useState('GOLD');
  const [silverRates, setSilverRates] = useState(null);
  const [metalLocked, setMetalLocked] = useState(false); // true once case is batched
  const [messageApi, contextHolder] = message.useMessage();
  const { canViewCreateModule, canEditModule, canEditField, canViewField } = usePermissions();
  const { user } = useAuth();
  const { getChannelRate, loading: deductionsLoading } = useKaratDeductions();
  
  const [showGstToggle, setShowGstToggle] = useState(false);
  const [showProcessingFeeToggle, setShowProcessingFeeToggle] = useState(true);
  
  // Track deduction loading state
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);

  // Field-level permission checks — show if viewable OR editable, disable if not editable
  const canFieldCustomerEdit = canEditField('walkInCase', 'customerId');
  const canFieldCustomerShow = canFieldCustomerEdit || canViewField('walkInCase', 'customerId');
  const canFieldGoldItemsEdit = canEditField('walkInCase', 'goldItems');
  const canFieldGoldItemsShow = canFieldGoldItemsEdit || canViewField('walkInCase', 'goldItems');
  const canFieldProcessingFeeEdit = canEditField('walkInCase', 'processingFee');
  const canFieldProcessingFeeShow = canFieldProcessingFeeEdit || canViewField('walkInCase', 'processingFee');
  const canFieldRemarksEdit = canEditField('walkInCase', 'remarks');
  const canFieldRemarksShow = canFieldRemarksEdit || canViewField('walkInCase', 'remarks');

  // Permission gate: create requires 'create', edit requires 'edit'
  const hasAccess = isEdit ? canEditModule('walkInCase') : canViewCreateModule('walkInCase');

  const loadLiveRate = async (silent = false) => {
    if (!silent) setRateLoading(true);
    const rateData = await fetchHallmarkGoldRate();
    if (rateData) setLiveRates(rateData);
    setRateLoading(false);
    return rateData;
  };

  const handleRefreshRate = async () => {
    invalidateGoldPriceCache();
    const rateData = await loadLiveRate();
    if (rateData?.rates && Array.isArray(rateData.rates)) {
      // Only update liveRates state; do NOT change existing gold item rates
      // When user changes karat, the new rates will be applied via handleGoldItemChange
      const rate22k = rateData.rates.find(r => r.karat === '22k');
      const newRate = getChannelRate(rate22k, 'walkIn');
      messageApi.success(`Live rates updated: ₹${newRate}/gm (22k, after deduction)`);
    } else {
      messageApi.warning('Could not refresh live rates');
    }
  };

  // Fetch live rate and setup defaults (both create and edit)
  useEffect(() => {
    // Only loads the rates. The default row's rate is filled in by the effect
    // below — resetting rows here raced it (StrictMode runs this twice, and a
    // late second response wiped the rate back to 0).
    loadLiveRate(true);
  }, []);

  // Load manual silver rates (used when Metal Type = Silver)
  useEffect(() => {
    fetchSilverRates().then((data) => { if (data) setSilverRates(data); });
  }, []);

  // Rate for a purity under a given metal (silver = manual rate, no deduction)
  const rateFor = (metal, purityName) => {
    if (metal === 'SILVER') {
      const r = (silverRates?.rates || []).find((x) => x.purity === purityName);
      return r?.perGram || 0;
    }
    const rateObj = liveRates?.rates?.find((r) => r.purity === purityName);
    return getChannelRate(rateObj, 'walkIn');
  };

  // Switching metal resets the items table to one blank row of that metal
  const handleMetalChange = (val) => {
    setMetalType(val);
    const defaultPurity = val === 'SILVER' ? SILVER_DEFAULT_PURITY : DEFAULT_PURITY;
    const rate = rateFor(val, defaultPurity);
    setGoldItems([recalcCaseGoldItem({
      ...EMPTY_GOLD_ITEM,
      purityName: defaultPurity,
      purity: 0,
      ratePerGram: rate,
      estimatedRatePerGram: rate,
    })]);
  };

  // Fetch GST Toggle Visibility
  useEffect(() => {
    if (user?.branch) {
      fetchBranchGstToggleVisibility(user.branch)
        .then(res => {
          if (res?.data?.data?.isVisible) {
            setShowGstToggle(true);
          }
        })
        .catch(err => console.error('Failed to fetch GST toggle visibility:', err));

      fetchBranchProcessingFeeToggleVisibility(user.branch)
        .then(res => {
          if (res?.data?.data?.isVisible !== undefined) {
            setShowProcessingFeeToggle(res.data.data.isVisible);
          }
        })
        .catch(err => console.error('Failed to fetch Processing Fee visibility:', err));
    }
  }, [user]);

  // Re-calculate defaults when deductions are loaded (for default purity)
  useEffect(() => {
    if (!deductionsLoading && !hasInitializedDefaults && liveRates?.rates && liveRates.rates.length > 0 && !isEdit) {
      const defaultRateObj = liveRates.rates.find(r => r.purity === DEFAULT_PURITY);
      // Apply deduction now that deductions are loaded
      const displayRate = getChannelRate(defaultRateObj, 'walkIn');
      // Fill only rows still without a rate, so nothing already typed is lost.
      setGoldItems((prev) => prev.map((item) => (
        (item.ratePerGram || item.estimatedRatePerGram)
          ? item
          : recalcCaseGoldItem({ ...item, ratePerGram: displayRate, estimatedRatePerGram: displayRate })
      )));
      setHasInitializedDefaults(true);
    }
  }, [deductionsLoading, hasInitializedDefaults, liveRates]);

  const watchedCustomerId = Form.useWatch('customerId', form);
  const watchedAgentId = Form.useWatch('agentId', form);

  // Resolve the selected customer in full: prefer the already-loaded list,
  // fall back to a fetch (an edited case's customer may be outside the
  // 50-row search window).
  useEffect(() => {
    if (!watchedCustomerId) return setSelectedCustomer(null);
    const fromList = customers.find((c) => c._id === watchedCustomerId);
    if (fromList) return setSelectedCustomer(fromList);
    getCustomer(watchedCustomerId)
      .then((res) => setSelectedCustomer(res.data))
      .catch(() => setSelectedCustomer(null));
  }, [watchedCustomerId, customers]);

  // Bank fields follow the picked customer's saved primary account.
  useCustomerBankAutofill(form, selectedCustomer, { keepExisting: isEdit });

  // Load customers
  useEffect(() => {
    fetchCustomers({ search: customerSearch, page: 1, limit: 50 })
      .then((res) => setCustomers(res.data || []))
      .catch(() => {});
  }, [customerSearch]);

  // Load agents
  useEffect(() => {
    fetchAgents({ status: 'ACTIVE', limit: 200 })
      .then((res) => setAgents(res.data || []))
      .catch(() => {});
  }, []);

  // Load existing case when editing
  useEffect(() => {
    if (!isEdit) return;
    const loadCase = async () => {
      setLoading(true);
      try {
        const res = await getWalkInCaseById(id);
        const c = res.data;
        form.setFieldsValue({
          customerId: c.customerId?._id || c.customerId,
          remarks: c.remarks,
          withGST: c.withGST !== false,
          reasonForSelling: c.reasonForSelling,
          referenceDetails: c.referenceDetails,
          referencePhone: c.referencePhone,
          alternatePhone: c.alternatePhone,
          bankAccountNumber: c.bankAccountNumber,
          ifscCode: c.ifscCode,
          bankBranchName: c.bankBranchName,
          goldOwnership: c.goldOwnership,
        });
        setCaseMeta({ code: c.caseId || null, date: c.createdAt || null });
        setMetalType(c.metalType === 'SILVER' ? 'SILVER' : 'GOLD');
        setMetalLocked(Boolean(c.inventoryBatchId)); // metal frozen once batched
        if (c.goldItems && c.goldItems.length > 0) {
          setGoldItems(c.goldItems.map((g) => {
            const normalizedRate = g.ratePerGram ?? g.estimatedRatePerGram ?? 0;
            return recalcCaseGoldItem({
              ...g,
              purityName: g.purityName || DEFAULT_PURITY,  // Ensure purityName is set
              ratePerGram: normalizedRate,
              estimatedRatePerGram: normalizedRate,
            });
          }));
        }
        if (c.goldImages && c.goldImages.length > 0) {
          setGoldImages(c.goldImages.map((img) => ({
            id: img.fileId || Math.random().toString(36).slice(2, 9),
            fileId: img.fileId,
            url: img.url,
            thumbnailUrl: img.thumbnailUrl,
            filePath: img.filePath,
            name: img.name,
            src: img.thumbnailUrl || img.url,
            type: img.type || 'gold'
          })));
        }
        if (c.processingFee != null) {
          setProcessingFee(c.processingFee);
        }
        if (c.roundingUpAmount != null) {
          setRoundingUpAmount(c.roundingUpAmount);
        }
        if (c.agentId) {
          form.setFieldsValue({ agentId: c.agentId._id || c.agentId });
        }
        if (c.agentCommissionPercent != null) {
          setAgentCommissionPercent(c.agentCommissionPercent);
        }
      } catch (err) {
        messageApi.error('Failed to load case');
      } finally {
        setLoading(false);
      }
    };
    loadCase();
  }, [id, isEdit]);

  // ── Gold Items helpers ──

  const handleGoldItemChange = (index, field, value) => {
    const updated = [...goldItems];
    const updatedItem = { ...updated[index], [field]: value };
    
    // Picking a karat only sets the rate — Purity (%) is always typed in by hand.
    if (field === 'purityName') {
      const displayRate = rateFor(metalType, value);
      updatedItem.ratePerGram = displayRate;
      updatedItem.estimatedRatePerGram = displayRate;
    }
    
    if (field === 'ratePerGram') {
      updatedItem.estimatedRatePerGram = value;
    } else if (field === 'estimatedRatePerGram') {
      updatedItem.ratePerGram = value;
    }
    
    // If dustDeduction is being manually changed, mark it as overridden
    // Otherwise, let it auto-recalculate
    const isDustDeductionChange = field === 'dustDeduction';
    const skipDustRecalc = isDustDeductionChange;
    
    // If a non-dustDeduction field changes, clear the override flag
    if (!isDustDeductionChange) {
      updatedItem._dustDeductionOverride = false;
    } else {
      updatedItem._dustDeductionOverride = true;
    }
    
    updated[index] = recalcCaseGoldItem(updatedItem, { skipDustRecalc });
    setGoldItems(updated);
  };

  const handleAddGoldItem = () => {
    const defaultPurity = metalType === 'SILVER' ? SILVER_DEFAULT_PURITY : DEFAULT_PURITY;
    let defaultRate = rateFor(metalType, defaultPurity);
    if (defaultRate === 0 && goldItems[0]) {
      defaultRate = goldItems[0]?.ratePerGram || 0;
    }
    const newItem = recalcCaseGoldItem({
      ...EMPTY_GOLD_ITEM,
      qty: goldItems.length + 1,
      purityName: defaultPurity,
      purity: 0,
      ratePerGram: defaultRate,
      estimatedRatePerGram: defaultRate,
    });
    setGoldItems([...goldItems, newItem]);
  };

  const handleRemoveGoldItem = (index) => {
    if (goldItems.length === 1) return;
    const updated = goldItems.filter((_, i) => i !== index);
    updated.forEach((item, idx) => (item.qty = idx + 1));
    setGoldItems(updated);
  };

  const totals = calculateCaseGoldTotals(goldItems);
  const effectiveProcessingFee = showProcessingFeeToggle ? processingFee : 0;
  // Gold value and fee stay exact; only the final amount rounds (nearest rupee).
  const processingFeeAmount = calculateCaseProcessingFee(totals.estimatedTotalAmount, effectiveProcessingFee);
  const approxFinalAmount = calculateCaseFinalAmount(totals.estimatedTotalAmount, processingFeeAmount, roundingUpAmount);
  const roundOffAmount = calculateCaseRoundOff(totals.estimatedTotalAmount, processingFeeAmount, roundingUpAmount, approxFinalAmount);

  const silverNotReady = metalType === 'SILVER'
    && (!silverRates || silverRates.notConfigured || !(silverRates.rates || []).some(r => r.perGram > 0));

  // ── Submit ──

  const handleSubmit = async (values) => {
    if (!goldItems.length) return messageApi.error('At least one gold item is required');
    const hasInvalid = goldItems.some(
      (g) => !g.itemName || g.grossWeight <= 0 || g.purity <= 0 || (g.ratePerGram ?? 0) <= 0,
    );
    if (hasInvalid) return messageApi.error('Please fill all required gold item fields (name, weight, purity, rate)');

    setSubmitting(true);
    try {
      const payload = {
        ...values,
        metalType,
        processingFee: effectiveProcessingFee,
        processingFeeAmount,
        roundingUpAmount,
        approxFinalAmount,
        agentCommissionPercent: values.agentId ? agentCommissionPercent : undefined,
        goldItems: goldItems.map((g, idx) => ({
          qty: idx + 1,
          itemName: g.itemName,
          purityName: g.purityName || DEFAULT_PURITY,
          purity: g.purity,
          grossWeight: g.grossWeight,
          stoneWaxDeduction: g.stoneWaxDeduction ?? g.deduction ?? 0,
          dustDeduction: g.dustDeduction,
          netWeight: g.netWeight,
          ratePerGram: g.ratePerGram ?? 0,
          totalPrice: g.totalPrice,
        })),
        goldImages: goldImages.map((img) => ({
          fileId: img.fileId,
          url: img.url,
          thumbnailUrl: img.thumbnailUrl,
          filePath: img.filePath,
          name: img.name,
          type: img.type || 'gold',
        })),
      };

      if (isEdit) {
        await updateWalkInCase(id, payload);
        messageApi.success('Case updated successfully');
        navigate(`/walk-in-cases/${id}`);
      } else {
        const res = await createWalkInCase(payload);
        allowLeave();
        messageApi.success('Case created successfully');
        navigate(`/walk-in-cases/${res.data._id}`);
      }
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to save case');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Gold Items Table Columns ──

  const goldColumns = [
    {
      title: <span style={{ fontSize: '10px' }}>Qty</span>,
      width: 56,
      render: (_, __, index) => (
        <Space align="center" size={4} style={{ display: 'flex', whiteSpace: 'nowrap' }}>
          {/* Always rendered (hidden with one row) so the number never shifts. */}
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => handleRemoveGoldItem(index)}
            size="small"
            style={{ padding: 0, fontSize: '10px', visibility: goldItems.length > 1 ? 'visible' : 'hidden' }}
            disabled={!canFieldGoldItemsEdit || goldItems.length <= 1}
          />
          <span>{index + 1}</span>
        </Space>
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Item Name</span>,
      dataIndex: 'itemName',
      render: (text, _, index) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          value={text}
          onChange={(e) => handleGoldItemChange(index, 'itemName', e.target.value)}
          size="small"
          style={{ fontSize: '10px' }}
          placeholder="e.g. Gold Chain"
          disabled={!canFieldGoldItemsEdit}
        />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Purity (%)</span>,
      dataIndex: 'purity',
      width: 90,
      render: (value, _, index) => (
        <InputNumber min={0} max={100} step={1} precision={2} value={value}
          onChange={(val) => handleGoldItemChange(index, 'purity', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false} placeholder="e.g. 90"
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Est. Rate / gm (₹)</span>,
      dataIndex: 'ratePerGram',
      width: 110,
      render: (value, _, index) => (
        <InputNumber min={0} max={100000}
          value={value ?? 0}
          onChange={(val) => handleGoldItemChange(index, 'ratePerGram', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false}
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Gross Weight (gm)</span>,
      dataIndex: 'grossWeight',
      width: 110,
      render: (value, _, index) => (
        <InputNumber min={0} step={0.0001} precision={4} value={value}
          onChange={(val) => handleGoldItemChange(index, 'grossWeight', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false}
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Stone/wax Deduction (gm)</span>,
      dataIndex: 'stoneWaxDeduction',
      width: 110,
      render: (value, _, index) => (
        <InputNumber min={0} step={0.0001} precision={4} value={value}
          onChange={(val) => handleGoldItemChange(index, 'stoneWaxDeduction', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false}
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Dust Deduction (gm)</span>,
      dataIndex: 'dustDeduction',
      width: 100,
      render: (value, _, index) => (
        <InputNumber min={0} step={0.0001} precision={4} value={value}
          onChange={(val) => handleGoldItemChange(index, 'dustDeduction', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false}
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Net Weight (gm)</span>,
      dataIndex: 'netWeight',
      width: 110,
      render: (value) => (
        <InputNumber value={value} precision={4} size="small" disabled
          style={{ width: '100%', fontSize: '10px', backgroundColor: 'white', color: 'black', borderColor: '#d9d9d9' }} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Total Price (₹)</span>,
      dataIndex: 'totalPrice',
      width: 150,
      render: (value) => <strong style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>₹ {formatAmount4(value)}</strong>,
    },
  ];

  if (!hasAccess) {
    return (
      <div className="container-fluid" style={{ padding: 24 }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Title level={3}>Access Denied</Title>
            <Text type="secondary">You don't have permission to {isEdit ? 'edit' : 'create'} Walk-In Cases.</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Card>
              <Row justify="space-between" align="middle" className="mb-3">
                <Col>
                  <Title level={3} style={{ margin: 0 }}>
                    {isEdit ? 'Edit Walk-In Purchase Case' : 'New Walk-In Purchase Case'}
                  </Title>
                </Col>
                <Col>
                  <Space wrap>
                    {!isEdit && (
                      <>
                      {metalType === 'GOLD' && liveRates?.rates && Array.isArray(liveRates.rates) && (
                        (() => {
                          const rateObj = liveRates.rates.find(r => r.karat === '22k');
                          const displayRate = rateObj ? getChannelRate(rateObj, 'walkIn') : 0;
                          return (
                            <Tooltip title={`Updated: ${liveRates.date} (incl. deduction)`}>
                              <Tag
                                color="gold"
                                icon={<GoldOutlined />}
                                style={{ cursor: 'default', fontSize: 13, padding: '2px 10px' }}
                              >
                                Live: {rateObj?.karat} ₹{displayRate?.toLocaleString('en-IN')}/gm
                              </Tag>
                            </Tooltip>
                          );
                        })()
                      )}
                      <Tooltip title="Refresh live gold rate and apply to all rows">
                        <Button
                          size="small"
                          icon={<SyncOutlined spin={rateLoading} />}
                          onClick={handleRefreshRate}
                          loading={rateLoading}
                        >
                          Refresh Rate
                        </Button>
                      </Tooltip>
                      </>
                    )}
                    {/* Consent to melt is taken at the counter, before the case
                        is finalised — so this prints from the live form. */}
                    <MeltingConsentButton
                      metalType={metalType}
                      customer={selectedCustomer}
                      user={user}
                      caseCode={caseMeta.code}
                      caseDate={caseMeta.date}
                      goldItems={goldItems}
                      goldImages={goldImages}
                    />
                  </Space>
                </Col>
              </Row>

              <Spin spinning={loading}>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  initialValues={{
                    withGST: true,
                  }}
                >
                  {canFieldGoldItemsShow && (
                    <>
                      <Divider orientation="left">{metalType === 'SILVER' ? 'Silver Items' : 'Gold Items'}</Divider>

                      <div style={{ marginBottom: 12 }}>
                        <Space align="center" wrap>
                          <Text strong>Metal Type:</Text>
                          <Radio.Group
                            value={metalType}
                            onChange={(e) => handleMetalChange(e.target.value)}
                            disabled={!canFieldGoldItemsEdit || metalLocked}
                            optionType="button"
                            buttonStyle="solid"
                            options={[{ label: 'Gold', value: 'GOLD' }, { label: 'Silver', value: 'SILVER' }]}
                          />
                          {metalType === 'SILVER' && silverRates?.date && (
                            <Tag color="default">Silver rate set: {silverRates.date} (manual)</Tag>
                          )}
                          {metalLocked && <Tag color="orange">Metal locked — already in an inventory batch</Tag>}
                        </Space>
                        {silverNotReady && (
                          <Alert
                            type="warning" showIcon style={{ marginTop: 8 }}
                            message="Silver rates are not set"
                            description="An admin must set today's silver rates in System → Silver Rates before silver items can be priced."
                          />
                        )}
                      </div>

                      <Table
                    dataSource={goldItems.map((item, idx) => ({ ...item, key: idx }))}
                    columns={goldColumns}
                    pagination={false}
                    size="small"
                    bordered
                    // Fixed layout: columns keep their width as values change (no
                    // jumping); Item Name takes the spare width, and below
                    // 1040px the table scrolls sideways instead of squeezing.
                    tableLayout="fixed"
                    scroll={{ x: 1040 }}
                    summary={() => (
                      <>
                        <Table.Summary.Row style={{ background: '#fafafa' }}>
                          <Table.Summary.Cell colSpan={4} align="right">
                            <strong style={{ fontSize: '11px' }}>Totals</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalGrossWeight)} gm</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalStoneDeduction)} gm</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalDustDeduction)} gm</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalNetWeight)} gm</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '12px' }}>₹ {formatAmount4(totals.estimatedTotalAmount)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        {showProcessingFeeToggle && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={8} align="right">
                              <Space>
                              <strong style={{ fontSize: '12px' }}>Processing Fee (%)</strong>
                                <InputNumber
                                  min={0}
                                  max={100}
                                  step={0.1}
                                  precision={2}
                                  value={processingFee}
                                  onChange={(val) => setProcessingFee(val || 0)}
                                  size="small"
                                  style={{ width: 80, fontSize: '10px' }}
                                  controls={false}
                                  disabled={!canFieldProcessingFeeEdit}
                                />
                            </Space>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '12px' }}>{formatProcessingFeeDisplay(processingFeeAmount)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        )}
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={8} align="right">
                            <Space>
                              <strong style={{ fontSize: '12px' }}>Rounding Up (₹)</strong>
                              <InputNumber
                                max={100000}
                                step={1}
                                precision={0}
                                value={roundingUpAmount}
                                onChange={(val) => setRoundingUpAmount(val || 0)}
                                size="small"
                                style={{ width: 80, fontSize: '10px' }}
                                controls={false}
                                disabled={!canFieldGoldItemsEdit}
                              />
                            </Space>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '12px' }}>{formatRoundingAmount(roundingUpAmount)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={8} align="right">
                            <Text type="secondary" style={{ fontSize: '12px' }}>Round Off (to nearest ₹)</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatRoundOff(roundOffAmount)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={8} align="right">
                            <strong style={{ fontSize: '12px' }}>Approx Final Amount</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '12px', color: '#1890ff' }}>₹ {formatWholeAmount(approxFinalAmount)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    )}
                  />

                  <Button type="dashed" onClick={handleAddGoldItem} icon={<PlusOutlined />} style={{ marginTop: 12, marginBottom: 24 }}
                    disabled={!canFieldGoldItemsEdit}>
                    Add Gold Item
                  </Button>
                  {/* Customer & Agent */}
                  <FormSection
                    title="Customer & Agent"
                    icon={<UserOutlined />}
                    description="Who is selling, and the agent who referred them"
                    extra={showGstToggle && (
                      <Tooltip title="Include GSTIN in generated documents.">
                        <span className="cf-gst-switch">
                          Tax details
                          <Form.Item name="withGST" valuePropName="checked" noStyle>
                            <Switch checkedChildren="With GSTIN" unCheckedChildren="Without GSTIN" />
                          </Form.Item>
                        </span>
                      </Tooltip>
                    )}
                  >
                    <Row gutter={20}>
                      {canFieldCustomerShow && (
                        <Col xs={24} lg={12}>
                          <Form.Item name="customerId" label="Customer" rules={[{ required: true, message: 'Please select a customer' }]}>
                            <CustomerSelect
                              customers={customers}
                              onSearch={setCustomerSearch}
                              selectedCustomer={selectedCustomer}
                              disabled={!canFieldCustomerEdit}
                            />
                          </Form.Item>
                        </Col>
                      )}
                      <Col xs={24} lg={12}>
                        <Row gutter={12}>
                          <Col xs={24} sm={16}>
                            <Form.Item name="agentId" label="Agent" tooltip="Optional — leave empty when there is no referring agent.">
                              <AgentSelect
                                agents={agents}
                                onAgentChange={(agent) => setAgentCommissionPercent(agent?.defaultCommissionPercent || 0)}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={8}>
                            <Form.Item label="Commission">
                              <InputNumber
                                size="large"
                                min={0}
                                max={100}
                                step={0.1}
                                value={agentCommissionPercent}
                                onChange={(val) => setAgentCommissionPercent(val || 0)}
                                addonAfter="%"
                                style={{ width: '100%' }}
                                disabled={!watchedAgentId}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </FormSection>

                  {/* Gold Images Upload Section */}
                  <FormSection
                    title={metalType === 'SILVER' ? 'Silver Images' : 'Gold Images'}
                    icon={<CameraOutlined />}
                    description="Photograph every item before it leaves the counter"
                  >
                    <div style={{ paddingBottom: 16 }}>
                      <GoldImageCapture
                        images={goldImages}
                        setImages={setGoldImages}
                        customerId={form.getFieldValue('customerId')}
                        driveImageType="walk-in-case"
                        driveFileNameBase="walk-in-case"
                        captureTargetKey="walkInCase.goldImages"
                        captureTargetLabel={metalType === 'SILVER' ? 'Silver Items — Walk-In Case' : 'Gold Items — Walk-In Case'}
                        captureFolderPath="/gold_item"
                        disabled={!canFieldGoldItemsEdit}
                      />
                    </div>
                  </FormSection>
                    </>
                  )}

                  {canFieldRemarksShow && (
                    <>
                    <FormSection
                      title="Additional Details"
                      icon={<ProfileOutlined />}
                      description="Printed on the purchase agreement"
                    >
                      <Row gutter={20}>
                        <Col xs={24} md={12}>
                          <Form.Item name="reasonForSelling" label="Reason for Selling Gold" rules={[{ required: true, message: 'Required' }]}>
                            <Input.TextArea rows={2} placeholder="e.g. Medical expenses, business need" disabled={!canFieldRemarksEdit} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="goldOwnership" label="Gold Ownership" rules={[{ required: true, message: 'Required' }]}>
                            <AutoComplete
                              size="large"
                              options={GOLD_OWNERSHIP_OPTIONS}
                              placeholder="Self, Spouse, Parents…"
                              filterOption={(input, opt) => opt.value.toLowerCase().includes(input.toLowerCase())}
                              disabled={!canFieldRemarksEdit}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="referenceDetails" label="Relative / Reference Person">
                            <Input size="large" prefix={<TeamOutlined className="text-muted" />} placeholder="Name and relation" disabled={!canFieldRemarksEdit} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item name="referencePhone" label="Reference Phone No.">
                            <Input size="large" prefix={<PhoneOutlined className="text-muted" />} placeholder="10-digit mobile number" maxLength={10} disabled={!canFieldRemarksEdit} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item name="alternatePhone" label="Alternate Phone No.">
                            <Input size="large" prefix={<PhoneOutlined className="text-muted" />} placeholder="10-digit mobile number" maxLength={10} disabled={!canFieldRemarksEdit} />
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item name="remarks" label="Remarks">
                            <Input.TextArea rows={2} placeholder="Optional notes" disabled={!canFieldRemarksEdit} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </FormSection>

                    <FormSection
                      title="Bank Details"
                      icon={<BankOutlined />}
                      description="Where a bank settlement payout is sent"
                    >
                      <SavedBankAccountSelect form={form} customer={selectedCustomer} disabled={!canFieldRemarksEdit} />
                      <Row gutter={20}>
                        <Col xs={24} md={8}>
                          <Form.Item name="bankAccountNumber" label="Account Number">
                            <Input size="large" placeholder="Bank account number" disabled={!canFieldRemarksEdit} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            name="ifscCode"
                            label="IFSC Code"
                            rules={[{ pattern: IFSC_PATTERN, message: 'Enter a valid IFSC, e.g. SBIN0001234' }]}
                          >
                            <IfscInput
                              size="large"
                              disabled={!canFieldRemarksEdit}
                              onResolved={(info) => form.setFieldsValue({ bankBranchName: bankBranchLabel(info) })}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item name="bankBranchName" label="Bank & Branch">
                            <Input size="large" placeholder="e.g. SBI - MG Road" disabled={!canFieldRemarksEdit} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </FormSection>
                    </>
                  )}

                  <div className="cf-actions">
                    <Button size="large" onClick={() => navigate('/walk-in-cases')}>Cancel</Button>
                    <Button size="large" type="primary" htmlType="submit" loading={submitting} icon={<CheckOutlined />}>
                      {isEdit ? 'Update Case' : 'Create Case'}
                    </Button>
                  </div>
                </Form>
              </Spin>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
